import type { CSSProperties } from 'react';
import { figmaAssets, type FigmaAssetName } from './figma-assets';

/** Each alias points to the original export at its original Figma size and color. */
export const iconAliases = {
  search: 'kit-icon-search',
  plus: 'kit-icon-plus',
  filter: 'kit-icon-filter',
  'chevron-down': 'kit-icon-chev-d',
  'chevron-left': 'kit-icon-chev-l',
  'chevron-right': 'kit-icon-chev-r',
  'chevrons-left': 'kit-icon-chevs-l',
  'chevrons-right': 'kit-icon-chevs-r',
  more: 'kit-icon-more',
  close: 'kit-icon-x',
  copy: 'kit-icon-copy',
  eye: 'kit-icon-eye',
  pencil: 'kit-icon-pencil',
  trash: 'kit-icon-trash',
  sort: 'kit-icon-sort',
  columns: 'kit-icon-columns',
  download: 'kit-icon-download',
  upload: 'kit-icon-upload',
  refresh: 'kit-icon-refresh',
  book: 'kit-icon-book',
  box: 'kit-icon-box',
  users: 'kit-icon-users',
  swap: 'kit-icon-swap',
  compare: 'kit-icon-compare',
  sliders: 'kit-icon-sliders',
  help: 'kit-icon-help',
  bell: 'kit-icon-bell',
  undo: 'kit-icon-undo',
  redo: 'kit-icon-redo',
  'check-circle': 'kit-icon-check-circle',
  'file-plus': 'kit-icon-file-plus',
  lock: 'kit-icon-lock',
  keyboard: 'kit-icon-keyboard',
  save: 'kit-icon-save',
  grip: 'kit-icon-grip',
  info: 'kit-icon-info',
  history: 'kit-icon-history',
  sparkles: 'kit-icon-sparkles',
  map: 'kit-icon-map',
  panel: 'kit-icon-panel',
  external: 'kit-icon-external',
  star: 'kit-icon-star',
  check: 'kit-icon-check1',
  barcode: 'kit-icon-barcode',
  'arrow-left': 'kit-icon-arrow-l',
  home: 'kit-icon-home',
  'page-panel': 'page-icon-panel',
  'page-search': 'page-search-icon',
  'page-home': 'page-icon',
  'page-book': 'page-icon-book',
  'page-box': 'page-icon1',
  'page-users': 'page-icon2',
  'page-swap': 'page-icon3',
  'page-compare': 'page-icon4',
  'page-upload': 'page-icon5',
  'page-close': 'page-icon-x',
  'page-help': 'page-icon6',
  'page-bell': 'page-icon-bell',
  'page-sparkles': 'page-icon7',
  'page-help-top': 'page-icon8',
  'page-bell-top': 'page-icon9',
  'page-import': 'page-icon10',
  'page-plus': 'page-icon-plus',
  'page-plus-white': 'page-icon-plus1',
  'page-sliders': 'page-icon11',
  'page-columns': 'page-icon12',
  'page-refresh': 'page-icon13',
  'page-sort': 'page-icon-sort',
  'page-eye': 'page-icon14',
  'page-pencil': 'page-icon15',
  'page-more': 'page-icon16',
  'page-chevrons-left': 'page-icon17',
  'page-chevron-left': 'page-icon18',
  'page-chevron-right': 'page-icon19',
  'page-chevrons-right': 'page-icon20',
  'page-chevron-down': 'page-chevron',
  'page-filter-chevron': 'page-chevron1',
  'page-book-muted': 'page-icon-book1',
  'page-check': 'page-icon-check',
  'button-plus-primary': 'kit-icon2',
  'button-plus-primary-icon': 'kit-icon3',
  'button-plus-secondary': 'kit-icon4',
  'button-plus-secondary-icon': 'kit-icon5',
  'button-plus-danger': 'kit-icon6',
  'button-plus-danger-icon': 'kit-icon7',
} as const satisfies Record<string, FigmaAssetName>;

export type IconName = keyof typeof iconAliases | FigmaAssetName;

export interface IconProps {
  name: IconName;
  className?: string;
  title?: string;
  style?: Omit<CSSProperties, 'width' | 'height'>;
}

/** Use a page-* alias for the catalog's 12/14/16px exports; generic icons are 20px. */
export function Icon({ name, className, title, style }: IconProps) {
  const assetName = name in iconAliases ? iconAliases[name as keyof typeof iconAliases] : name as FigmaAssetName;
  const asset = figmaAssets[assetName];
  return (
    <img
      className={['sv-icon', className].filter(Boolean).join(' ')}
      src={asset.src}
      width={asset.width}
      height={asset.height}
      alt={title ?? ''}
      aria-hidden={title ? undefined : true}
      draggable={false}
      data-figma-asset={assetName}
      style={style}
    />
  );
}

export { figmaAssets, type FigmaAssetName };
